import { AxiosResponse } from 'axios';
import { api } from './config';
import {
  Customer, CreateCustomer, MonthlyReport,
  TruckDetailedReport, DailyProfit, AiModel, CreateAiModel, UpdateAiModel,
  SelectAiModel
} from '../../types';

export const customerApi = {
  getAll: (): Promise<AxiosResponse<Customer[]>> =>
    api.get('/customer'),
    
  getById: (id: number): Promise<AxiosResponse<Customer>> =>
    api.get(`/customer/${id}`),
    
  create: (data: CreateCustomer): Promise<AxiosResponse<Customer>> =>
    api.post('/customer', data),
    
  update: (id: number, data: CreateCustomer): Promise<AxiosResponse<void>> =>
    api.put(`/customer/${id}`, data),
    
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/customer/${id}`)
};

// User Settings API
export const userSettingsApi = {
  getCurrentUserSettings: (): Promise<AxiosResponse<any>> =>
    api.get('/usersettings'),
    
  updateCurrentUserSettings: (data: any): Promise<AxiosResponse<void>> =>
    api.put('/usersettings', data),
    
  createUserSettings: (data: any): Promise<AxiosResponse<any>> =>
    api.post('/usersettings', data)
};

export const locationApi = {
  getProvincesWithDistricts: () =>
    api.get('/rm2fee/provinces-with-districts'),
};

// Reports API
export const reportsApi = {
  getMonthlyReport: (year: number, month: number): Promise<AxiosResponse<MonthlyReport>> =>
    api.get('/reports/monthly', { params: { year, month } }),
  getTruckDetailedReport: (year: number, month: number): Promise<AxiosResponse<TruckDetailedReport[]>> =>
    api.get('/reports/truck-detailed', { params: { year, month } }),
  getTaxRate: (): Promise<AxiosResponse<{ rate: number }>> =>
    api.get('/reports/tax-rate'),
  getDailyProfitData: (days: number = 30): Promise<AxiosResponse<DailyProfit[]>> =>
    api.get('/reports/daily-profit', { params: { days } })
};

export const systemApi = {
  exportAllData: (): Promise<AxiosResponse<Blob>> =>
    api.get('/system/export-all-data', {
      responseType: 'blob'
    }),
    
  importData: (file: File): Promise<AxiosResponse<any>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/system/import-data', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  getSystemInfo: (): Promise<AxiosResponse<any>> =>
    api.get('/system/system-info')
};

// Driver API
export const aiModelApi = {
  getAll: (): Promise<AxiosResponse<AiModel[]>> =>
  api.get('/aimodel'),
  getSelected: (): Promise<AxiosResponse<AiModel>> =>
    api.get('/aimodel/selected'),
  create: (data: CreateAiModel): Promise<AxiosResponse<AiModel>> =>
    api.post('/aimodel', data),
  update: (id: number, data: UpdateAiModel): Promise<AxiosResponse<void>> =>
    api.put(`/aimodel/${id}`, data),
  select: (data: SelectAiModel): Promise<AxiosResponse<void>> =>
    api.post('/aimodel/select', data),
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/aimodel/${id}`)
};

// VCOMSAT Sync API
export const vcomsatSyncApi = {
  syncVehicles: (): Promise<AxiosResponse<any>> =>
    api.post('/vcomsatsync/sync-vehicles'),
    
  syncJourney: (vcomsatCarId: number, fromDate?: string, toDate?: string): Promise<AxiosResponse<any>> =>
  api.post(`/vcomsatsync/sync-journey/${vcomsatCarId}`, null, {
  params: { fromDate, toDate }
    }),
    
  getSyncedVehicles: (): Promise<AxiosResponse<any[]>> =>
    api.get('/vcomsatsync/vehicles'),
  
  getJourneyData: (vcomsatCarDetailId: number, fromDate?: string, toDate?: string): Promise<AxiosResponse<any[]>> =>
    api.get(`/vcomsatsync/journey/${vcomsatCarDetailId}`, {
    params: { fromDate, toDate }
  }),

  // New endpoints for vehicle tracking
  getLatestPositions: (): Promise<AxiosResponse<VehiclePosition[]>> =>
    api.get('/vcomsatsync/latest-positions'),
    
  getLatestPosition: (vcomsatCarDetailId: number): Promise<AxiosResponse<VehiclePosition>> =>
    api.get(`/vcomsatsync/latest-position/${vcomsatCarDetailId}`),
    
  // Route points - GPS coordinates along the vehicle's path
  getRoutePoints: (vcomsatCarDetailId: number, fromDate?: string, toDate?: string): Promise<AxiosResponse<VcomsatRoutePoint[]>> =>
    api.get(`/vcomsatsync/route-points/${vcomsatCarDetailId}`, {
      params: { fromDate, toDate }
    }),
    
  // Get current journey - from when vehicle left home to current position
  // The backend searches backwards to find the last home departure
  getCurrentJourney: (
    vcomsatCarDetailId: number,
    homeLat: number = 10.27218,
    homeLon: number = 106.35878,
    homeRadiusKm: number = 0.5
  ): Promise<AxiosResponse<CurrentJourneyResponse>> =>
    api.get(`/vcomsatsync/current-journey/${vcomsatCarDetailId}`, {
      params: { homeLat, homeLon, homeRadiusKm }
    }),
    
  // Manually sync route points for a vehicle
  syncRoutePoints: (vcomsatCarId: number, fromDate?: string, toDate?: string): Promise<AxiosResponse<any>> =>
    api.post(`/vcomsatsync/sync-route-points/${vcomsatCarId}`, null, {
      params: { fromDate, toDate }
    })
};

// Vehicle Position type
export interface VehiclePosition {
  vcomsatCarDetailId: number;
  vcomsatCarID: number;
  carPlate: string;
  vehicleId?: number;
  vehicleLicensePlate?: string;
  latitude: number;
longitude: number;
  speed: number;
  status: number;
  address: string;
  realTime: string;
}

// Route Point type - GPS coordinates along the vehicle's path
export interface VcomsatRoutePoint {
  id: number;
  realTime: string;
  latitude: number;
  longitude: number;
  address: string;
}

// Current Journey Response type
export interface CurrentJourneyResponse {
  vcomsatCarDetailId: number;
  carPlate: string;
  hasJourney: boolean;
  departureTime?: string;
  totalPoints: number;
  homeLat: number;
  homeLon: number;
  homeRadiusKm: number;
  routePoints: VcomsatRoutePoint[];
  message?: string;
}

// Fuel Efficiency API
